import { px2rem } from '../../core';
import { LinearProgress, useTheme } from '@mui/material';
import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import CloseIcon from '../icon/CloseIcon';
import { UploadOptions } from '@mnz/builder';
import PlusIcon from '../icon/PlusIcon';

interface MultipleUploaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSelect'> {
    value?: string;
    imageList?: string[];
    onSelect: (image: string) => void;
    onSuccess: (url: string) => void;
    onRemove: (image: string) => void;
    uploadImage: (file: File, options?: Partial<UploadOptions>) => Promise<string>;
}

const MultipleUploader: FC<MultipleUploaderProps> = ({
    value,
    imageList,
    uploadImage,
    onSelect,
    onSuccess,
    onRemove,
    ...props
}) => {
    const theme = useTheme();
    const [message, setMessage] = useState<null | string>(null);
    const [progress, setProgress] = useState<number | null>(null);

    const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
        const { files } = e.target;
        const file = files?.item(0);
        if (!file) {
            setMessage('Please select image.');
            return;
        }
        setMessage('');
        setProgress(0);
        e.target.value = '';
        try {
            const res = await uploadImage(file, {
                onUploadProgress: setProgress,
            });
            onSuccess(res);
            setProgress(null);
        } catch (error: unknown) {
            setProgress(null);
            setMessage('Somethings went wrong.');
        }
    };

    const handleRemove = (image: string) => {
        setProgress(null);
        onRemove(image);
    };
    return (
        <div {...props}>
            {(!imageList || imageList.length === 0) && (
                <div
                    css={{
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        css={{
                            paddingBottom: `${((85 / 300) * 100).toFixed(4)}%`,
                        }}
                    />
                    <div
                        css={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            color: theme.palette.primary50,
                            objectFit: 'cover',
                            objectPosition: 'center',
                            borderWidth: 1,
                            borderStyle: progress !== null ? 'solid' : 'dashed',
                            borderColor: progress !== null ? theme.palette.success.main : theme.palette.primary50,
                            borderRadius: 4,
                            cursor: 'pointer',
                        }}
                    >
                        <input
                            css={{
                                opacity: 0,
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 1,
                            }}
                            type="file"
                            accept={'image/*'}
                            onChange={handleUpload}
                        />
                        <div
                            css={{
                                flexGrow: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                cursor: 'pointer',
                            }}
                        >
                            {progress === null ? (
                                <Fragment>
                                    <PlusIcon />
                                    <p css={{ fontWeight: 600, marginTop: px2rem(8) }}>Upload Image</p>
                                </Fragment>
                            ) : (
                                <div
                                    css={{
                                        width: '50%',
                                    }}
                                >
                                    <LinearProgress
                                        variant="determinate"
                                        color={'success'}
                                        value={progress}
                                        sx={{ height: px2rem(6), borderRadius: 4 }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {imageList && imageList.length === 1 && progress === null && (
                <div>
                    <div
                        css={{
                            position: 'relative',
                        }}
                    >
                        <div
                            css={{
                                paddingBottom: `${((85 / 300) * 100).toFixed(4)}%`,
                            }}
                        />
                        <div
                            css={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                color: theme.palette.primary50,
                                objectFit: 'cover',
                                objectPosition: 'center',
                                borderWidth: 1,
                                borderColor: theme.palette.primary20,
                                borderRadius: 4,
                                cursor: 'pointer',
                            }}
                        >
                            <img
                                src={imageList[0]}
                                css={{
                                    objectFit: 'contain',
                                    height: '100%',
                                }}
                            />
                            <div
                                css={{
                                    position: 'absolute',
                                    right: px2rem(4),
                                    top: px2rem(4),
                                    zIndex: 10,
                                    background: '#FFFFFF',
                                    boxShadow: '0.747621px 2.24286px 5.23335px rgba(0, 0, 0, 0.15)',
                                    borderRadius: 4,
                                    width: px2rem(30),
                                    height: px2rem(30),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: theme.palette.primary.main,
                                    cursor: 'pointer',
                                }}
                                onClick={() => handleRemove(imageList[0])}
                            >
                                <CloseIcon />
                            </div>
                        </div>
                    </div>
                    <div css={{ marginTop: 8, position: 'relative', display: 'inline-flex' }}>
                        <input
                            css={{
                                opacity: 0,
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 1,
                                cursor: 'pointer',
                            }}
                            type="file"
                            accept={'image/*'}
                            onChange={handleUpload}
                        />
                        <div
                            css={{
                                color: theme.palette.success.main,
                                textDecoration: 'underline',
                                fontWeight: 'bold',
                                fontSize: 12,
                            }}
                        >
                            <PlusIcon /> Upload another logo
                        </div>
                    </div>
                </div>
            )}

            {imageList && ((imageList.length > 0 && progress !== null) || imageList.length > 1) && (
                <Fragment>
                    <div
                        css={{
                            background: theme.palette.primary10,
                            borderRadius: 10,
                            padding: `10px 6px`,
                            overflowY: 'hidden',
                            display: 'flex',
                        }}
                    >
                        {imageList.length < 3 && (
                            <div
                                css={{
                                    width: '33.33333%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    objectPosition: 'center',
                                    borderColor:
                                        progress === null ? theme.palette.primary50 : theme.palette.success.main,
                                    borderWidth: 1,
                                    borderRadius: 4,
                                    borderStyle: progress === null ? 'dashed' : 'solid',
                                    marginLeft: 4,
                                    marginRight: 4,
                                    position: 'relative',
                                }}
                            >
                                <div css={{ paddingBottom: '100%' }} />
                                {progress !== null && (
                                    <div
                                        css={{
                                            width: '100%',
                                            height: '100%',
                                            position: 'absolute',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            padding: 8,
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <LinearProgress
                                            variant="determinate"
                                            color={'success'}
                                            value={progress}
                                            sx={{ height: px2rem(6), borderRadius: 4 }}
                                        />
                                    </div>
                                )}
                                {progress === null && (
                                    <Fragment>
                                        <input
                                            css={{
                                                opacity: 0,
                                                position: 'absolute',
                                                width: '100%',
                                                height: '100%',
                                                zIndex: 1,
                                                cursor: 'pointer',
                                            }}
                                            type="file"
                                            accept={'image/*'}
                                            onChange={handleUpload}
                                        />
                                        <div
                                            css={{
                                                position: 'absolute',
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: theme.palette.primary50,
                                            }}
                                        >
                                            <PlusIcon />
                                        </div>
                                    </Fragment>
                                )}
                            </div>
                        )}
                        {imageList.map((i) => (
                            <div
                                key={i}
                                css={{
                                    width: '33.33333%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderColor: i === value ? theme.palette.success.main : theme.palette.primary40,
                                    borderWidth: 1,
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    marginLeft: 4,
                                    marginRight: 4,
                                }}
                                onClick={() => {
                                    onSelect(i);
                                }}
                            >
                                <div css={{ paddingBottom: '100%' }} />
                                <div
                                    css={{
                                        position: 'absolute',
                                        width: '100%',
                                        height: '100%',
                                        objectPosition: 'center',
                                        display: 'flex',
                                    }}
                                >
                                    <img
                                        src={i}
                                        css={{
                                            objectFit: 'contain',
                                            width: '100%',
                                            height: '100%',
                                        }}
                                    />
                                </div>
                                <div
                                    css={{
                                        position: 'absolute',
                                        right: px2rem(4),
                                        top: px2rem(4),
                                        zIndex: 10,
                                        background: '#FFFFFF',
                                        boxShadow: '0.747621px 2.24286px 5.23335px rgba(0, 0, 0, 0.15)',
                                        borderRadius: 4,
                                        width: px2rem(30),
                                        height: px2rem(30),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: theme.palette.primary.main,
                                    }}
                                    onClick={() => {
                                        handleRemove(i);
                                    }}
                                >
                                    <CloseIcon />
                                </div>
                            </div>
                        ))}
                    </div>
                </Fragment>
            )}
            {message && <div>{message}</div>}
        </div>
    );
};

export default MultipleUploader;
